import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  Settings,
  LayoutDashboard,
  Radio,
  Factory,
  Globe,
  Server,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import betaAPI from "v2/api";
import { SUPPORTED_LANGUAGES } from "i18n";
import LanguageSelect from "components/common/LanguageSelect";
import { useIsElectron } from "hooks/useIsElectron";
import { useAppUpdate } from "hooks/useAppUpdate";
import WearableOpsModal from "../WearableOpsModal";
import CaseDashboardModal from "../CaseDashboard";

const UserProfileButton = ({ role }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWearableOps, setShowWearableOps] = useState(false);
  const [showCaseDashboard, setShowCaseDashboard] = useState(false);
  // Port the auto-open should connect to (from a plug-in event); null for a
  // manual open, where the modal picks the single wearable itself.
  const [autoConnectPath, setAutoConnectPath] = useState(null);
  const dropdownRef = useRef(null);
  // Timestamp of the last manual close, so a device re-enumerating right after
  // the operator dismisses the modal doesn't immediately reopen it.
  const closedAtRef = useRef(0);
  const isElectron = useIsElectron();
  const appUpdate = useAppUpdate();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Fetch current user on mount
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await betaAPI.getCurrentUser();
        setUser(response.data);
        // Merge with existing localStorage user to preserve fields
        // (e.g. is_verified) that the /me/ endpoint may not return
        const existing = JSON.parse(localStorage.getItem("user") || "{}");
        localStorage.setItem(
          "user",
          JSON.stringify({ ...existing, ...response.data })
        );
      } catch (err) {
        console.error("Failed to fetch user", err);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);

  const getInitials = () => {
    if (!user) return "?";
    const first = user.fname?.[0] || "";
    const last = user.lname?.[0] || "";
    return (
      (first + last).toUpperCase() || user.email?.[0]?.toUpperCase() || "?"
    );
  };

  const getDisplayName = () => {
    if (!user) return t("navigation.userMenu.defaultName");
    if (user.fname && user.lname) {
      return `${user.fname} ${user.lname}`;
    }
    return (
      user.fname ||
      user.email?.split("@")[0] ||
      t("navigation.userMenu.defaultName")
    );
  };

  const getUserTypeLabel = () => {
    if (user?.is_superadmin)
      return t("navigation.userMenu.userType.superAdmin");
    if (role === "admin") return t("navigation.userMenu.userType.admin");
    if (role === "coach") return t("navigation.userMenu.userType.coach");
    return t("navigation.userMenu.userType.user");
  };

  const isSuperAdmin = user?.is_superadmin === true;

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("user");
    localStorage.removeItem("selectedWorkspace");
    navigate("/auth/sign-in");
    setOpen(false);
  };

  const handleAccountSettings = () => {
    navigate("/v2/settings");
    setOpen(false);
  };

  const handleSuperUserDashboard = () => {
    navigate("/v2/admin/default");
    setOpen(false);
  };

  const handleManufacturing = () => {
    navigate("/v2/admin/manufacturing");
    setOpen(false);
  };

  const handleWearableOps = () => {
    setAutoConnectPath(null);
    setShowWearableOps(true);
    setOpen(false);
  };

  const handleCaseDashboard = () => {
    setShowCaseDashboard(true);
    setOpen(false);
  };

  const closeWearableOps = () => {
    closedAtRef.current = Date.now();
    setShowWearableOps(false);
    setAutoConnectPath(null);
  };

  // Auto-open Wearable Operations when a wearable is plugged in. The modal then
  // auto-connects to it. Guarded so it won't reopen on top of itself, and not
  // within a few seconds of a manual close (covers a device re-enumerating).
  useEffect(() => {
    const serial = window.electronAPI?.serial;
    if (!isElectron || !serial?.onWearableAttached) return undefined;
    const off = serial.onWearableAttached((port) => {
      setShowWearableOps((alreadyOpen) => {
        if (alreadyOpen) return true;
        if (Date.now() - closedAtRef.current < 6000) return false;
        setAutoConnectPath(port?.path || null);
        return true;
      });
    });
    return off;
  }, [isElectron]);

  if (loading) {
    return (
      <div className="flex items-center gap-3 px-2 py-1.5">
        <div className="h-9 w-9 animate-pulse rounded-full bg-gray-200" />
        <div className="flex flex-col gap-1">
          <div className="h-4 w-20 animate-pulse rounded bg-gray-200" />
          <div className="h-3 w-12 animate-pulse rounded bg-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition duration-150 hover:bg-[#F5F5F5]"
      >
        {/* Avatar */}
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-[#E07A3A] to-[#D66B2E] text-sm font-semibold text-white ${
            isSuperAdmin ? "ring-2 ring-[#E07A3A] ring-offset-2" : ""
          }`}
        >
          {getInitials()}
        </div>

        {/* Name + Role */}
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-gray-900">
            {getDisplayName()}
          </span>
          <span className="text-xs text-gray-500">{getUserTypeLabel()}</span>
        </div>

        {/* Chevron */}
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.15 }}
        >
          <ChevronDown size={16} className="text-gray-400" />
        </motion.div>
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={{ duration: 0.15 }}
              className="absolute right-0 top-full z-50 mt-2 w-[280px] overflow-hidden rounded-[14px] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)]"
            >
              {/* User Info Header */}
              <div className="border-b border-gray-100 px-4 py-4">
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-[#E07A3A] to-[#D66B2E] text-base font-semibold text-white ${
                      isSuperAdmin ? "ring-2 ring-[#E07A3A] ring-offset-2" : ""
                    }`}
                  >
                    {getInitials()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-semibold text-gray-900">
                      {getDisplayName()}
                    </p>
                    <p className="truncate text-xs text-gray-500">
                      {user?.email}
                    </p>
                    {/* Internal id is only useful to support/admin staff */}
                    {isSuperAdmin && user?.id && (
                      <p className="mt-0.5 truncate font-mono text-[10px] text-gray-400">
                        {t("navigation.userMenu.userId", { id: user.id })}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Language */}
              {SUPPORTED_LANGUAGES.length > 1 && (
                <>
                  <div className="border-t border-gray-100" />
                  <div className="px-4 py-3">
                    <div className="mb-2 flex items-center gap-3">
                      <Globe size={16} className="text-gray-500" />
                      <span className="text-sm font-medium text-gray-700">
                        {t("common.language.label")}
                      </span>
                    </div>
                    <LanguageSelect />
                  </div>
                </>
              )}

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Actions */}
              <div className="py-2">
                {/* SuperUser Dashboard - only for super admins */}
                {isSuperAdmin && (
                  <div
                    onClick={handleSuperUserDashboard}
                    className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <LayoutDashboard size={16} className="text-[#E07A3A]" />
                    <span className="text-sm font-medium text-gray-700">
                      {t("navigation.userMenu.superUserDashboard")}
                    </span>
                  </div>
                )}

                {/* Manufacturing Admin - only for super admins */}
                {isSuperAdmin && (
                  <div
                    onClick={handleManufacturing}
                    className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <Factory size={16} className="text-[#E07A3A]" />
                    <span className="text-sm font-medium text-gray-700">
                      {t("navigation.userMenu.manufacturing")}
                    </span>
                  </div>
                )}

                {/* Wearable Operations - only in Electron */}
                {isElectron && (
                  <div
                    onClick={handleWearableOps}
                    className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <Radio size={16} className="text-[#E07A3A]" />
                    <span className="text-sm font-medium text-gray-700">
                      {t("navigation.userMenu.wearableOperations")}
                    </span>
                  </div>
                )}

                {/* Case dashboard - only in Electron (SSH to a case) */}
                {isElectron && (
                  <div
                    onClick={handleCaseDashboard}
                    className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition duration-150 hover:bg-[#F9FAFB]"
                  >
                    <Server size={16} className="text-[#E07A3A]" />
                    <span className="text-sm font-medium text-gray-700">
                      {t("navigation.userMenu.caseDashboard")}
                    </span>
                  </div>
                )}

                {/* Account Settings */}
                <div
                  onClick={handleAccountSettings}
                  className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition duration-150 hover:bg-[#F9FAFB]"
                >
                  <Settings size={16} className="text-gray-500" />
                  <span className="text-sm font-medium text-gray-700">
                    {t("navigation.userMenu.accountSettings")}
                  </span>
                </div>

                {/* Logout */}
                <div
                  onClick={handleLogout}
                  className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition duration-150 hover:bg-red-50"
                >
                  <LogOut size={16} className="text-red-500" />
                  <span className="text-sm font-medium text-red-600">
                    {t("navigation.userMenu.logout")}
                  </span>
                </div>
              </div>

              {/* Desktop app version + manual update check */}
              {appUpdate.available && (
                <div className="flex items-center justify-between border-t border-gray-100 px-4 py-2.5">
                  <span className="text-[11px] text-gray-400">
                    Version {appUpdate.currentVersion}
                  </span>
                  <UpdateStatusAction appUpdate={appUpdate} />
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Wearable Operations Modal */}
      <WearableOpsModal
        show={showWearableOps}
        onClose={closeWearableOps}
        autoConnectPath={autoConnectPath}
      />

      {/* Case Dashboard Modal (Electron only) */}
      {isElectron && (
        <CaseDashboardModal
          show={showCaseDashboard}
          onClose={() => setShowCaseDashboard(false)}
        />
      )}
    </div>
  );
};

// Right-hand side of the version footer: a live status, or a check button.
const UpdateStatusAction = ({ appUpdate }) => {
  const { status, check, install } = appUpdate;
  const text = "text-[11px] font-medium";
  switch (status.state) {
    case "checking":
      return <span className={`${text} text-gray-400`}>Checking…</span>;
    case "downloading":
      return (
        <span className={`${text} text-gray-400`}>
          Downloading {status.version} ({status.percent || 0}%)
        </span>
      );
    case "downloaded":
      return (
        <button
          onClick={install}
          className={`${text} text-[#E07A3A] hover:underline`}
        >
          Restart to update
        </button>
      );
    case "up-to-date":
      return <span className={`${text} text-gray-400`}>Up to date</span>;
    default:
      return (
        <button
          onClick={check}
          className={`${text} text-gray-500 hover:text-gray-700 hover:underline`}
        >
          Check for updates
        </button>
      );
  }
};

export default UserProfileButton;
